import React, { useEffect, useState } from 'react';
import { getDashboardStats } from '../api/client';
import { ClipboardList, Users, DollarSign, AlertTriangle, CheckCircle, Clock, Zap } from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, color, sub }) => (
  <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #F3F4F6' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 6 }}>{label}</p>
        <p style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}>{value}</p>
        {sub && <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>{sub}</p>}
      </div>
      <div style={{ background: color + '18', borderRadius: 10, padding: 10 }}>
        <Icon size={22} color={color} />
      </div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const colors = {
    open: ['#FEF3C7', '#92400E'],
    assigned: ['#DBEAFE', '#1D4ED8'],
    in_progress: ['#D1FAE5', '#065F46'],
    completed: ['#F0FDF4', '#166534'],
    cancelled: ['#F3F4F6', '#374151'],
    urgent: ['#FEE2E2', '#DC2626'],
    high: ['#FEF3C7', '#92400E'],
    normal: ['#EFF6FF', '#3B82F6'],
  };
  const [bg, text] = colors[status] || ['#F3F4F6', '#374151'];
  return (
    <span style={{ background: bg, color: text, padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>
      {status?.replace('_', ' ')}
    </span>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDashboardStats().then(r => setStats(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#6B7280' }}>Loading dashboard...</div>;
  if (!stats) return null;

  const { status_counts, technicians, revenue_this_month, completed_this_month, todays_jobs, recent_jobs, urgent_jobs } = stats;

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 24 }}>Dashboard</h1>

      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
        <StatCard icon={ClipboardList} label="Open Jobs" value={status_counts.open + status_counts.assigned} color="#F59E0B" sub={`${status_counts.in_progress} in progress`} />
        <StatCard icon={CheckCircle} label="Completed This Month" value={completed_this_month} color="#10B981" sub={`${todays_jobs} scheduled today`} />
        <StatCard icon={Users} label="Technicians" value={`${technicians.available}/${technicians.total}`} color="#3B82F6" sub="available right now" />
        <StatCard icon={DollarSign} label="Revenue This Month" value={`$${revenue_this_month.toLocaleString('en', { minimumFractionDigits: 0 })}`} color="#8B5CF6" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
        {/* Recent Jobs */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #F3F4F6' }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: '#111827', marginBottom: 16 }}>Recent Jobs</h2>
          {recent_jobs.length === 0 ? (
            <p style={{ color: '#9CA3AF', fontSize: 14, textAlign: 'center', padding: 20 }}>No jobs yet</p>
          ) : (
            <div>
              {recent_jobs.map(job => (
                <div key={job.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 0', borderBottom: '1px solid #F9FAFB' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{job.job_number}</div>
                    <div style={{ color: '#6B7280', fontSize: 13 }}>{job.customer_name} · {job.equipment_type || 'General'}</div>
                  </div>
                  <StatusBadge status={job.status} />
                  <StatusBadge status={job.priority} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Urgent Jobs */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #F3F4F6' }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: '#111827', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={16} color="#EF4444" /> Urgent Jobs
          </h2>
          {urgent_jobs.length === 0 ? (
            <p style={{ color: '#9CA3AF', fontSize: 14, textAlign: 'center', padding: 20 }}>No urgent jobs</p>
          ) : (
            urgent_jobs.map(job => (
              <div key={job.id} style={{ background: '#FEF2F2', borderRadius: 8, padding: 12, marginBottom: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#DC2626' }}>{job.job_number}</div>
                <div style={{ fontSize: 13, color: '#374151' }}>{job.customer_name}</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{job.address}</div>
              </div>
            ))
          )}

          {/* Job Status Summary */}
          <div style={{ marginTop: 20, borderTop: '1px solid #F3F4F6', paddingTop: 16 }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Job Status</h3>
            {Object.entries(status_counts).map(([status, count]) => (
              <div key={status} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <StatusBadge status={status} />
                <span style={{ fontWeight: 700, color: '#111827' }}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
